import { useMemo } from 'react'

import { useFetchZoneMatchesQuery } from 'api/zones/zoneMatches'
import { useDebounced } from 'hooks/useDebounced'

import type { ZoneMatch } from 'types/modules/zones/zone'

export type LatLng = { latitude: number | string | null | undefined; longitude: number | string | null | undefined }

export type ZoneMatches = {
  pricing: ZoneMatch[]
  tax: ZoneMatch[]
  service: ZoneMatch[]
  isLoading: boolean
  // The lists answer for the current point: nothing is settling and the lookup has returned.
  isResolved: boolean
}

const NO_MATCHES: ZoneMatch[] = []
const TYPING_QUIET_MS = 300

const byName = (a: ZoneMatch, b: ZoneMatch) => a.name.localeCompare(b.name, 'en-US')

// Number('') is 0, so a blank coordinate field would otherwise look up the equator.
const toCoordinate = (value: number | string | null | undefined): number | null => {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return null
  const coordinate = Number(value)
  return Number.isFinite(coordinate) ? coordinate : null
}

/**
 * The active zones whose boundary contains the point, per purpose, by name. Coordinates settle briefly
 * before the lookup, so typing a latitude is one request; until the new point's zones arrive there are none.
 */
export const useGeoZoneMatches = ({ latitude, longitude }: LatLng, { skip = false } = {}): ZoneMatches => {
  const lat = toCoordinate(latitude)
  const lng = toCoordinate(longitude)
  // A string, not an object: a fresh object every render would re-arm the quiet period forever.
  const liveKey = lat === null || lng === null ? '' : `${lat},${lng}`
  const pointKey = useDebounced(liveKey, TYPING_QUIET_MS)
  const point = useMemo(() => {
    if (!pointKey) return null
    const [pointLatitude, pointLongitude] = pointKey.split(',').map(Number)
    return { latitude: pointLatitude, longitude: pointLongitude }
  }, [pointKey])

  // currentData, not data: data keeps the previous point's zones while the new point loads.
  const { currentData, isFetching } = useFetchZoneMatchesQuery(point ?? { latitude: 0, longitude: 0 }, { skip: skip || !point })
  const isSettling = Boolean(liveKey) && liveKey !== pointKey
  const matches = useMemo(
    () => (liveKey && point && !isSettling && currentData ? [...currentData].sort(byName) : NO_MATCHES),
    [liveKey, point, isSettling, currentData]
  )
  const isLookingUp = Boolean(liveKey) && !skip && (isSettling || isFetching)
  const isResolved = Boolean(liveKey) && !skip && !isSettling && currentData !== undefined

  return useMemo(
    () => ({
      pricing: matches.filter(zone => zone.isPricingZone),
      tax: matches.filter(zone => zone.isTaxZone),
      service: matches.filter(zone => zone.isEligibilityZone),
      isLoading: isLookingUp,
      isResolved,
    }),
    [matches, isLookingUp, isResolved]
  )
}
